import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { GasRoute } from '@/features/gasRoutes/types';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  cachedItemByIdSelector,
  infiniteQueryOptionsFn,
} from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { Sector, SectorGrid, SectorRequestPayload } from './types';

export const sectorsApi = createApi({
  reducerPath: 'sectorsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/sectors`,
  }),
  tagTypes: ['Sectors', 'Sector'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getSectorsI: builder.infiniteQuery<
      SectorGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 20 }),
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['Sector', 'Sectors'],
    }),
    getSectors: builder.query<SectorGrid[], FiltersGrid | void>({
      query: params => {
        return {
          url: '/query',
          params,
        };
      },
      providesTags: () => ['Sector', 'Sectors'],
    }),
    getSectorById: builder.query<Sector, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: result => [{ type: 'Sector', id: result?.id }],
    }),
    getGasRoutesBySector: builder.infiniteQuery<
      (GasRoute & { isOperating: boolean })[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 20 }),
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/gas-routes/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['Sector', 'Sectors'],
    }),
    createSector: builder.mutation<Sector, SectorRequestPayload>({
      query: data => ({
        url: '',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Sectors', 'Sector'],
    }),
    updateSector: builder.mutation<Sector, SectorRequestPayload>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Sectors', 'Sector'],
    }),
  }),
});

export const {
  useGetSectorByIdQuery,
  useCreateSectorMutation,
  useUpdateSectorMutation,
  useGetSectorsQuery,
} = sectorsApi;

export const useGetSectorsInfiniteQuery = normalizeInfiniteQueryData(
  sectorsApi.useGetSectorsIInfiniteQuery,
);

export const useGetGasRoutesBySectorInfiniteQuery = normalizeInfiniteQueryData(
  sectorsApi.useGetGasRoutesBySectorInfiniteQuery,
);

const getCachedSectorByIdSelector = cachedItemByIdSelector(
  'sectorsApi',
  'getSectors',
);

export const useGetCachedSectorByIdSelector = id =>
  useSelector(getCachedSectorByIdSelector(id));
